import { useEffect, useMemo, useState } from "react";
import api, { eur, num } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSignature, Zap, Clock, TrendingUp, TrendingDown } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const METRICS = [
  { key: "count", label: "N° Preventivi", color: "#E3B341" },
  { key: "valore", label: "Valore €", color: "#22c55e" },
  { key: "kw", label: "kW", color: "#38bdf8" },
  { key: "tempo", label: "Tempo", color: "#a78bfa" },
];

const fmtMin = (m) => {
  if (m == null) return "—";
  const h = Math.floor(m / 60);
  const mm = Math.round(m % 60);
  return h ? `${h}h ${mm}m` : `${mm}m`;
};
const dm = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : "");

function Delta({ v, avg }) {
  if (v == null) return <span className="text-xs text-muted-foreground">—</span>;
  const up = v >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-xs">
      <span className={`inline-flex items-center gap-1 font-semibold ${up ? "text-emerald-500" : "text-red-500"}`}>
        <Icon className="h-3.5 w-3.5" />{up ? "+" : ""}{v.toLocaleString("it-IT", { maximumFractionDigits: 1 })}%
      </span>
      <span className="font-normal text-muted-foreground">vs media settimanale da inizio anno ({avg})</span>
    </span>
  );
}

function Slot({ icon: Icon, label, value, children, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground"><Icon className="h-4 w-4 text-primary" /> {label}</div>
      <div className="mt-1 font-heading text-3xl font-black tracking-tight">{value}</div>
      <div className="mt-1">{children}</div>
    </Card>
  );
}

export default function PreventiviAnalytics() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 0 = tutto l'anno
  const [metric, setMetric] = useState("count");
  const [data, setData] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams({ year: String(year) });
    if (month) q.append("month", String(month));
    api.get(`/quotes/analytics?${q.toString()}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [year, month]);

  const chart = useMemo(() => {
    if (!data) return [];
    const src = month ? data.daily : data.monthly;
    const prepMin = data?.weekly_slots?.prep_minutes_per_quote ?? 10;
    return (src || []).map((r) => ({ label: r.label, value: metric === "tempo" ? Math.round((r.count || 0) * prepMin) : r[metric] }));
  }, [data, month, metric]);

  const s = data?.weekly_slots;
  const years = data?.meta?.years?.length ? data.meta.years : [year];
  const mColor = METRICS.find((m) => m.key === metric)?.color || "#E3B341";

  return (
    <div className="space-y-4" data-testid="preventivi-analytics">
      {s?.week_from && (
        <p className="text-xs text-muted-foreground" data-testid="week-range">
          Settimana di riferimento (lun–ven): <span className="font-semibold text-foreground">dal {dm(s.week_from)} al {dm(s.week_to)}</span>
        </p>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Slot icon={FileSignature} label="Preventivi (settimana)" value={s ? num(s.week_count) : "—"} testid="slot-week-count"><Delta v={s?.pct_count} avg={s ? `${num(s.avg_week_count)} prev/sett` : "—"} /></Slot>
        <Slot icon={Zap} label="kW preventivati (settimana)" value={s ? `${num(Math.round(s.week_kw))} kW` : "—"} testid="slot-week-kw"><Delta v={s?.pct_kw} avg={s ? `${num(Math.round(s.avg_week_kw))} kW/sett` : "—"} /></Slot>
        <Slot icon={Clock} label="Tempo prep. / giorno (settimana)" value={s ? fmtMin(s.week_prep_minutes_per_day) : "—"} testid="slot-prep-time">
          <span className="text-xs text-muted-foreground">Media da inizio anno: <b className="text-foreground">{s ? fmtMin(s.avg_prep_minutes_per_day) : "—"}</b>/giorno · ≈ {s?.prep_minutes_per_quote ?? 10} min/preventivo</span>
        </Slot>
      </div>

      <Card className="p-4" data-testid="preventivi-chart-card">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h2 className="mr-auto font-heading text-lg font-bold tracking-tight">Andamento preventivi</h2>
          <div className="inline-flex rounded-md border border-border p-0.5">
            {METRICS.map((m) => (
              <button key={m.key} onClick={() => setMetric(m.key)} data-testid={`chart-metric-${m.key}`} className={`rounded px-2.5 py-1 text-xs font-semibold transition-colors ${metric === m.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{m.label}</button>
            ))}
          </div>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-[150px]" data-testid="chart-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Tutto l'anno</SelectItem>
              {MONTHS.map((mn, i) => <SelectItem key={i} value={String(i + 1)}>{mn}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[110px]" data-testid="chart-year"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="prevGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={mColor} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={mColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={52} tickFormatter={(v) => (metric === "valore" ? `${Math.round(v / 1000)}k` : metric === "tempo" ? fmtMin(v) : num(v))} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "hsl(var(--foreground))" }}
                labelFormatter={(label) => (month ? `${label} ${MONTHS[month - 1].slice(0, 3).toLowerCase()} ${year}` : `${label} ${year}`)}
                formatter={(v) => [metric === "valore" ? eur(v) : metric === "kw" ? `${num(v)} kW` : metric === "tempo" ? fmtMin(v) : num(v), METRICS.find((m) => m.key === metric)?.label]}
              />
              <Area type="monotone" dataKey="value" stroke={mColor} strokeWidth={2.5} fill="url(#prevGrad)" dot={false} activeDot={{ r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{month ? `${MONTHS[month - 1]} ${year} · giorno per giorno` : `${year} · mese per mese`}</p>
      </Card>
    </div>
  );
}
